'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';
import { cn } from '@/shared/lib/cn';

interface ColorPickerDialogProps {
  color: string;
  onChange: (color: string) => void;
  title?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
  overlayClassName?: string;
}

export function ColorPickerDialog({
  color,
  onChange,
  title,
  open,
  onOpenChange,
  className,
  overlayClassName,
}: ColorPickerDialogProps) {
  const t = useTranslations('colorPicker');
  const tCommon = useTranslations('common');
  const [tempColor, setTempColor] = useState(color);
  const [isDialogReady, setIsDialogReady] = useState(false);

  useEffect(() => {
    setTempColor(color);
  }, [color]);

  // Prevent native color picker from opening immediately on dialog open
  useEffect(() => {
    if (open) {
      setIsDialogReady(false);
      // Longer delay to prevent click event from propagating to color input on iOS
      const timer = setTimeout(() => setIsDialogReady(true), 500);
      return () => clearTimeout(timer);
    }
    setIsDialogReady(false);
    return undefined;
  }, [open]);

  const handleApply = () => {
    onChange(tempColor);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setTempColor(color); // Reset to original color
    onOpenChange(false);
  };

  // Architectural colors - muted and sophisticated tones suitable for facade design
  const presetColors = [
    // Neon markers (highlighter colors)
    '#39FF14', // Neon Green
    '#FFEA00', // Neon Yellow
    '#FF073A', // Neon Red
    // Whites and light grays
    '#FFFFFF',
    '#F8F8F8',
    '#F0F0F0',
    '#E8E8E8',
    '#D8D8D8',
    '#C8C8C8',
    '#B0B0B0',
    '#989898',
    // Warm grays and beiges
    '#F5F3F0',
    '#EAE7E3',
    '#DED9D3',
    '#D2CCC4',
    '#C4BDB4',
    '#B5ADA3',
    '#A69B8F',
    '#8B7F72',
    // Cool grays
    '#E8EAEC',
    '#D8DCDF',
    '#C7CDD2',
    '#B4BAC0',
    '#9FA6AD',
    '#8A929A',
    '#6F7780',
    '#565D65',
    // Muted blues
    '#E3E8EC',
    '#D1DADF',
    '#BFCAD2',
    '#A8B7C2',
    '#90A3B0',
    '#768A98',
    '#5D7080',
    '#4A5A68',
    // Muted greens
    '#E5E8E3',
    '#D7DCD3',
    '#C5CDC1',
    '#B0BBAA',
    '#97A68F',
    '#7E9074',
    '#65775B',
    '#4D5E45',
    // Sage and olive
    '#E4E7E2',
    '#D4DAD0',
    '#C2CABC',
    '#ADB7A5',
    '#96A28C',
    '#7D8A70',
    '#647155',
    '#4C5841',
    // Yellows and golds
    '#FFF9E6',
    '#FFF3CC',
    '#FFECB3',
    '#FFE599',
    '#FFDB66',
    '#FFCD3D',
    '#F4BE00',
    '#C99700',
    // Muted terracotta and clay
    '#E8E3DF',
    '#DCD3CA',
    '#CCC0B3',
    '#BAAA9A',
    '#A6927F',
    '#8E7764',
    '#755D4B',
    '#5D4638',
    // Muted browns
    '#E6E2DD',
    '#D9D2C9',
    '#C8BDB0',
    '#B5A695',
    '#9E8D79',
    '#86735D',
    '#6D5A43',
    '#55442D',
    // Charcoals and darks
    '#60605E',
    '#52524F',
    '#454541',
    '#393934',
    '#2D2D28',
    '#22221D',
    '#181813',
    '#0F0F0C',
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'w-full max-w-[calc(100%-2rem)] sm:max-w-md max-h-[90vh] p-0 overflow-hidden',
          // Override default mobile positioning - center vertically
          '!top-[50%] !translate-y-[-50%]',
          className
        )}
        overlayClassName={overlayClassName}
        onPointerDownOutside={(e) => {
          // Prevent dialog from closing when touching outside on mobile
          // User must explicitly click Cancel or Apply
          e.preventDefault();
        }}
        onInteractOutside={(e) => {
          // Prevent dialog from closing when interacting outside
          e.preventDefault();
        }}
      >
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle>{title || t('title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('description')}
          </DialogDescription>
        </DialogHeader>

        {/* Use native scrolling instead of ScrollArea for better mobile support */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] overscroll-contain">
          <div className="p-6 space-y-4">
            {/* Native color picker */}
            <div className="space-y-2">
              <Label htmlFor="color-input">{t('colorPickerLabel')}</Label>
              <input
                id="color-input"
                type="color"
                value={tempColor}
                onChange={(e) => setTempColor(e.target.value)}
                onClick={(e) => {
                  // Prevent native picker from opening if dialog just opened
                  if (!isDialogReady) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onPointerDown={(e) => {
                  // Prevent native picker from opening if dialog just opened
                  if (!isDialogReady) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onTouchStart={(e) => {
                  // Prevent native picker from opening if dialog just opened (iOS specific)
                  if (!isDialogReady) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onFocus={(e) => {
                  // Prevent focus from triggering picker on iOS
                  if (!isDialogReady) {
                    e.target.blur();
                  }
                }}
                className={cn(
                  'w-full h-12 rounded-lg border border-border transition-all',
                  isDialogReady ? 'cursor-pointer' : 'pointer-events-none'
                )}
                style={{
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none',
                  backgroundColor: tempColor,
                  border: '1px solid hsl(var(--border))',
                }}
              />
            </div>

            {/* Preset colors */}
            <div className="space-y-2">
              <Label>{t('presetColorsLabel')}</Label>
              <div className="grid grid-cols-8 gap-1">
                {presetColors.map((presetColor) => (
                  <button
                    key={presetColor}
                    onClick={() => setTempColor(presetColor)}
                    className={`
                      w-full aspect-square rounded-md border-2 transition-all
                      ${
                        tempColor === presetColor
                          ? 'border-primary scale-105 ring-2 ring-primary/20'
                          : 'border-border hover:border-muted-foreground'
                      }
                    `}
                    style={{ backgroundColor: presetColor }}
                    title={presetColor}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 pb-6 pt-0">
          <Button variant="outline" onClick={handleCancel} className="flex-1">
            {tCommon('cancel')}
          </Button>
          <Button onClick={handleApply} className="flex-1">
            {tCommon('apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
